import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Trophy, MapPin, Download, Lock, Loader2, Smartphone, Wallet, Shirt, ShieldCheck, ShieldAlert } from 'lucide-react';

function downloadCSV(rows, columns, filename) {
    const header = columns.map((c) => c.label).join(',') + '\n';
    const body = rows.map((r) => columns.map((c) => `"${String(c.render ? c.render(r) : (r[c.key] ?? '')).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}

const REWARD_BADGE = {
    smartphone: { label: 'Smartphone', cls: 'bg-emerald-100 text-emerald-700', icon: Smartphone },
    cash: { label: '₹500 Cash', cls: 'bg-blue-100 text-blue-700', icon: Wallet },
    tshirt: { label: 'T-Shirt', cls: 'bg-amber-100 text-amber-700', icon: Shirt },
};

export default function AdminContest() {
    const [tab, setTab] = useState('weekly');
    return (
        <div data-testid="admin-contest-page">
            <div className="mb-6">
                <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 flex items-center gap-2">
                    <Trophy className="h-7 w-7 text-brand-yellowDark" /> Contest Leaderboards
                </h1>
                <p className="text-sm text-slate-500 mt-1">Weekly reward winners and state-round qualifiers, computed live from arcade game points.</p>
            </div>

            <div className="flex gap-2 mb-6 border-b border-slate-200">
                {[{ k: 'weekly', l: 'Weekly Rewards', icon: Trophy }, { k: 'state', l: 'State Qualifiers', icon: MapPin }].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`contest-tab-${t.k}`}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${tab === t.k ? 'border-brand-blue text-brand-blue' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                        <t.icon className="h-4 w-4" /> {t.l}
                    </button>
                ))}
            </div>

            {tab === 'weekly' ? <WeeklyRewardsTab /> : <StateQualifiersTab />}
        </div>
    );
}

function WeeklyRewardsTab() {
    const [data, setData] = useState(null);
    const [weekStart, setWeekStart] = useState('');
    const [busy, setBusy] = useState(false);

    const load = (ws) => {
        setData(null);
        api.get('/admin/contest/weekly', { params: ws ? { week_start: ws, limit: 1000 } : { limit: 1000 } })
            .then((r) => { setData(r.data); if (!weekStart) setWeekStart(r.data.week_start); })
            .catch(() => toast.error('Failed to load weekly leaderboard'));
    };
    useEffect(() => { load(weekStart); /* eslint-disable-next-line */ }, [weekStart]);

    const freeze = async () => {
        if (!window.confirm('Freeze this week and lock in the winners? Winners will be marked and cannot win the same reward again.')) return;
        setBusy(true);
        try {
            const r = await api.post('/admin/contest/weekly/freeze', { week_start: data.week_start });
            toast.success(r.data.already_frozen ? 'Week already frozen' : `Frozen — ${r.data.winners?.length || 0} winners locked`);
            load(data.week_start);
        } catch { toast.error('Freeze failed'); } finally { setBusy(false); }
    };

    const cols = [
        { key: 'rank', label: 'Rank' },
        { key: 'full_name', label: 'Learner' },
        { key: 'mobile', label: 'Mobile' },
        { key: 'state_branch', label: 'State/Branch', render: (r) => r.state_branch || '—' },
        { key: 'weekly_score', label: 'Weekly Score' },
        { key: 'cumulative', label: 'Cumulative' },
        { key: 'plays', label: 'Games Played' },
        { key: 'past_reward', label: 'Past Reward', render: (r) => r.past_reward || '—' },
        { key: 'eligible', label: 'Eligible', render: (r) => (r.eligible ? 'Yes' : `No (${(r.ineligible_reasons || []).join('; ')})`) },
        { key: 'allocated_reward', label: 'Reward Won', render: (r) => (r.allocated_reward ? REWARD_BADGE[r.allocated_reward].label : '—') },
    ];

    if (!data) return <Loading />;
    const rows = data.rows || [];
    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Contest week</span>
                    <select value={data.week_start} onChange={(e) => setWeekStart(e.target.value)} className="mt-1 block px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="contest-week-select">
                        {(data.weeks || []).map((w) => <option key={w.week_start} value={w.week_start}>{w.label}</option>)}
                    </select>
                </label>
                <div className="flex-1" />
                {data.frozen ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-brand-yellow text-xs font-bold" data-testid="contest-frozen-badge"><Lock className="h-3.5 w-3.5" /> Frozen {data.frozen_at ? `· ${new Date(data.frozen_at).toLocaleDateString()}` : ''}</span>
                ) : (
                    <button onClick={freeze} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-black text-brand-yellow text-sm font-bold disabled:opacity-60" data-testid="contest-freeze-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Freeze & lock winners
                    </button>
                )}
                <button onClick={() => downloadCSV(rows, cols, `weekly-${data.week_start}.csv`)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="contest-weekly-export">
                    <Download className="h-4 w-4" /> Export CSV
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="contest-weekly-summary">
                <Stat label="Players this week" value={data.summary.players} />
                <Stat label="Eligible" value={data.summary.eligible} />
                <Stat label="Smartphone" value={`${data.summary.smartphone}/10`} tone="emerald" />
                <Stat label="Cash ₹500" value={`${data.summary.cash}/100`} tone="blue" />
                <Stat label="T-Shirt" value={`${data.summary.tshirt}/100`} tone="amber" />
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="contest-weekly-table">
                <div className="overflow-auto max-h-[62vh]">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0 text-[11px] uppercase tracking-wider text-slate-600">
                            <tr>{cols.map((c) => <th key={c.key} className="py-3 px-3 text-left whitespace-nowrap">{c.label}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r) => (
                                <tr key={r.user_id} className={`hover:bg-slate-50 ${r.allocated_reward ? 'bg-brand-yellow/5' : ''}`} data-testid={`contest-weekly-row-${r.rank}`}>
                                    <td className="py-2.5 px-3 font-bold text-slate-700">#{r.rank}</td>
                                    <td className="py-2.5 px-3"><Link to={`/admin/learners/${r.user_id}`} className="font-semibold text-brand-blue hover:underline">{r.full_name}</Link></td>
                                    <td className="py-2.5 px-3 text-slate-600">{r.mobile || '—'}</td>
                                    <td className="py-2.5 px-3 text-slate-600">{r.state_branch || '—'}</td>
                                    <td className="py-2.5 px-3 font-bold text-slate-900">{r.weekly_score}</td>
                                    <td className="py-2.5 px-3 text-slate-500">{r.cumulative}</td>
                                    <td className="py-2.5 px-3 text-slate-500">{r.plays}</td>
                                    <td className="py-2.5 px-3 text-slate-500 capitalize">{r.past_reward || '—'}</td>
                                    <td className="py-2.5 px-3">{r.eligible ? <span className="text-emerald-600 text-xs font-semibold">Eligible</span> : <span className="text-rose-500 text-xs" title={(r.ineligible_reasons || []).join('; ')}>{(r.ineligible_reasons || []).join('; ')}</span>}</td>
                                    <td className="py-2.5 px-3">{r.allocated_reward ? <RewardBadge k={r.allocated_reward} /> : <span className="text-slate-300">—</span>}</td>
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={cols.length} className="py-12 text-center text-sm text-slate-500">No game activity in this week yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StateQualifiersTab() {
    const [data, setData] = useState(null);
    const [cluster, setCluster] = useState('');
    const [busy, setBusy] = useState(false);

    const load = () => {
        setData(null);
        api.get('/admin/contest/state-qualifiers', { params: cluster ? { cluster } : {} })
            .then((r) => setData(r.data))
            .catch(() => toast.error('Failed to load state qualifiers'));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [cluster]);

    const freeze = async () => {
        if (!window.confirm('Freeze the final state qualifier list?')) return;
        setBusy(true);
        try {
            const r = await api.post('/admin/contest/state-qualifiers/freeze');
            toast.success(`Frozen — ${r.data.total_qualifiers} qualifiers locked`);
            load();
        } catch { toast.error('Freeze failed'); } finally { setBusy(false); }
    };

    const exportAll = () => {
        const rows = (data.clusters || []).flatMap((c) => c.rows.map((r) => ({ ...r, cluster: c.cluster })));
        const cols = [
            { key: 'cluster', label: 'State/Branch' }, { key: 'state_rank', label: 'State Rank' },
            { key: 'full_name', label: 'Learner' }, { key: 'mobile', label: 'Mobile' },
            { key: 'final_digital_score', label: 'Final Digital Score' }, { key: 'plays', label: 'Games Played' },
            { key: 'qualifier_status', label: 'Qualifier Status' },
        ];
        downloadCSV(rows, cols, 'state-qualifiers.csv');
    };

    if (!data) return <Loading />;
    const clusterOptions = data.clusters.map((c) => c.cluster);
    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="text-xs font-semibold text-slate-600">State / Branch cluster</span>
                    <select value={cluster} onChange={(e) => setCluster(e.target.value)} className="mt-1 block px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="contest-cluster-select">
                        <option value="">All clusters</option>
                        {clusterOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <div className="flex-1" />
                {data.frozen ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-brand-yellow text-xs font-bold"><Lock className="h-3.5 w-3.5" /> Final list frozen</span>
                ) : (
                    <button onClick={freeze} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-black text-brand-yellow text-sm font-bold disabled:opacity-60" data-testid="contest-state-freeze-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Freeze final list
                    </button>
                )}
                <button onClick={exportAll} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="contest-state-export">
                    <Download className="h-4 w-4" /> Export CSV
                </button>
            </div>

            {data.clusters.map((c) => (
                <div key={c.cluster} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`contest-cluster-${c.cluster}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
                        <div>
                            <div className="font-display font-bold text-slate-900">{c.cluster} <span className="text-xs font-normal text-slate-400">· {c.region}{c.centre ? ` · ${c.centre}` : ''}</span></div>
                            <div className="text-xs text-slate-500 mt-0.5">Quota {c.digital_quota} + {c.wildcard_quota} wildcard · {c.participants} participants · <span className="text-emerald-600 font-semibold">{c.digital_qualifiers} qualifiers</span>{c.wild_cards ? ` · ${c.wild_cards} wildcard` : ''}</div>
                        </div>
                    </div>
                    {c.rows.length === 0 ? <div className="py-8 text-center text-sm text-slate-400">No participants mapped to this cluster.</div> : (
                        <div className="overflow-auto max-h-[45vh]">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0 text-[11px] uppercase tracking-wider text-slate-600">
                                    <tr><th className="py-2.5 px-3 text-left">Rank</th><th className="py-2.5 px-3 text-left">Learner</th><th className="py-2.5 px-3 text-left">Mobile</th><th className="py-2.5 px-3 text-right">Final Score</th><th className="py-2.5 px-3 text-right">Plays</th><th className="py-2.5 px-3 text-left">Status</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {c.rows.slice(0, 200).map((r) => (
                                        <tr key={r.user_id} className="hover:bg-slate-50">
                                            <td className="py-2.5 px-3 font-bold text-slate-700">{r.state_rank ? `#${r.state_rank}` : '—'}</td>
                                            <td className="py-2.5 px-3"><Link to={`/admin/learners/${r.user_id}`} className="font-semibold text-brand-blue hover:underline">{r.full_name}</Link></td>
                                            <td className="py-2.5 px-3 text-slate-600">{r.mobile || '—'}</td>
                                            <td className="py-2.5 px-3 text-right font-bold text-slate-900">{r.final_digital_score}</td>
                                            <td className="py-2.5 px-3 text-right text-slate-500">{r.plays}</td>
                                            <td className="py-2.5 px-3"><StatusBadge status={r.qualifier_status} reasons={r.ineligible_reasons} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function RewardBadge({ k }) {
    const b = REWARD_BADGE[k]; if (!b) return null; const Icon = b.icon;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${b.cls}`}><Icon className="h-3 w-3" /> {b.label}</span>;
}

function StatusBadge({ status, reasons }) {
    const map = {
        'Digital Qualifier': 'bg-emerald-100 text-emerald-700',
        'Wild Card': 'bg-violet-100 text-violet-700',
        'Not Selected': 'bg-slate-100 text-slate-500',
        'Not Eligible': 'bg-rose-100 text-rose-600',
    };
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${map[status] || 'bg-slate-100 text-slate-500'}`} title={status === 'Not Eligible' ? (reasons || []).join('; ') : ''}>
        {status === 'Digital Qualifier' ? <ShieldCheck className="h-3 w-3" /> : status === 'Not Eligible' ? <ShieldAlert className="h-3 w-3" /> : null} {status}
    </span>;
}

function Stat({ label, value, tone }) {
    const tones = { emerald: 'text-emerald-600', blue: 'text-blue-600', amber: 'text-amber-600' };
    return (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-3 text-center">
            <div className={`font-display font-extrabold text-2xl ${tones[tone] || 'text-brand-blue'}`}>{value}</div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
        </div>
    );
}

function Loading() {
    return <div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
}
